import { graphql } from '@/gql';
import type { AddPaymentSourceMutationVariables } from '@/gql/graphql';
import { mutationExecute } from '@/Utils/graphqlAPI';
import { getStripeIntentRequiresActionFromGraphqlError } from '@/services/payment/stripeIntentRequiresAction';
import { getStripeInstance, type StripeIntentKind } from '@/use/stripe';

type AddPaymentSourceInput = AddPaymentSourceMutationVariables & {
  visitorId?: string;
  /** Discriminates whether setupIntentId holds a SetupIntent ('setup') or registration PaymentIntent ('payment'). */
  intentKind?: StripeIntentKind;
};

const executeAddPaymentSourceMutation = async ({
  userId,
  stripeToken,
  setupIntentId,
  confirmationTokenId,
  cardHolderPhoneNumber,
  cardHolderType,
  visitorId,
  plan,
  isDirectExpress,
  activateAccount,
  subscriptionStartAt,
  isGourmetRegistration,
  intentKind = 'setup',
}: AddPaymentSourceInput): Promise<boolean> => {
  const isPaymentIntent = intentKind === 'payment';
  let query: any = {
    mutation: graphql(`
      mutation AddPaymentSource(
        $userId: String!
        $stripeToken: String
        $setupIntentId: String
        $paymentIntentId: String
        $confirmationTokenId: String
        $cardHolderPhoneNumber: String
        $cardHolderType: String
        $plan: String
        $isDirectExpress: Boolean
        $activateAccount: Boolean
        $subscriptionStartAt: String
        $isGourmetRegistration: Boolean
      ) {
        addPaymentSource(
          stripeToken: $stripeToken
          setupIntentId: $setupIntentId
          paymentIntentId: $paymentIntentId
          confirmationTokenId: $confirmationTokenId
          cardHolderPhoneNumber: $cardHolderPhoneNumber
          cardHolderType: $cardHolderType
          userId: $userId
          plan: $plan
          isDirectExpress: $isDirectExpress
          activateAccount: $activateAccount
          subscriptionStartAt: $subscriptionStartAt
          isGourmetRegistration: $isGourmetRegistration
        )
      }
    `),
    variables: {
      userId,
      stripeToken,
      setupIntentId: isPaymentIntent ? null : setupIntentId,
      paymentIntentId: isPaymentIntent ? setupIntentId : null,
      confirmationTokenId,
      cardHolderPhoneNumber,
      cardHolderType,
      plan,
      isDirectExpress,
      activateAccount,
      subscriptionStartAt,
      isGourmetRegistration,
    },
  };

  if (visitorId) {
    query = {
      ...query,
      context: {
        headers: {
          'visitor-id': visitorId,
        },
      },
    };
  }

  const response = await mutationExecute(query);

  return response.data?.addPaymentSource ?? false;
};

export const addPaymentSource = async (params: AddPaymentSourceInput): Promise<boolean> => {
  try {
    return await executeAddPaymentSourceMutation(params);
  } catch (error) {
    if (!params.confirmationTokenId) {
      throw error;
    }

    const requiresAction = getStripeIntentRequiresActionFromGraphqlError(error);
    if (!requiresAction) {
      throw error;
    }

    const stripe = getStripeInstance();
    if (!stripe) {
      throw error;
    }

    const { error: nextActionError } = await stripe.handleNextAction({
      clientSecret: requiresAction.clientSecret,
    });

    if (nextActionError) {
      throw nextActionError;
    }

    const { confirmationTokenId, ...retryPostData } = params;
    return executeAddPaymentSourceMutation({
      ...retryPostData,
      setupIntentId: requiresAction.intentId ?? retryPostData.setupIntentId,
    });
  }
};
